import { OWNER_GRANT, parseAccessToken, type AccessRole } from '@okwhere/shared';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { api } from '../../lib/api';
import { appConfig } from '../../lib/config';
import { accessToken, setAccessToken, subscribeAccessToken } from '../../lib/storage';

/** Trades an access code (permanent or guest) for a token kept in this browser. */
export async function unlock(code: string): Promise<void> {
  const { token } = await api.post<{ token: string }>('/api/access', { code });
  setAccessToken(token);
}

/**
 * Asks the server once per page load and token whether the token still works, and forgets it
 * if its guest code was revoked or the permanent code changed.
 */
const checked = new Set<string>();
function checkWithServer(token: string): void {
  if (checked.has(token)) return;
  checked.add(token);
  api
    .get<{ role: AccessRole | null }>('/api/access')
    .then(({ role }) => {
      if (!role && accessToken() === token) setAccessToken(null);
    })
    .catch(() => undefined);
}

// Timers can't wait longer than this (~24.8 days); a later check just waits again.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

/**
 * What this browser may do: `locked` until it enters an access code; then `role` (owner or
 * guest) until `expiresAt`. A guest whose code ends is locked again without a reload.
 * `ready` turns true once we know whether the server needs a code at all.
 */
export function useAccess(): { ready: boolean; locked: boolean; role: AccessRole | null; expiresAt: number | null } {
  const token = useSyncExternalStore(subscribeAccessToken, accessToken);
  const [required, setRequired] = useState<boolean | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const parsed = token ? parseAccessToken(token) : null;
  const expiresAt = parsed?.expiresAt ?? null;

  useEffect(() => {
    let active = true;
    void appConfig().then((config) => {
      if (active) setRequired(config.accessRequired);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (token && required) checkWithServer(token);
  }, [token, required]);

  useEffect(() => {
    if (expiresAt === null || expiresAt <= now) return;
    const timer = window.setTimeout(() => setNow(Date.now()), Math.min(MAX_TIMEOUT_MS, Math.max(0, expiresAt - Date.now()) + 500));
    return () => window.clearTimeout(timer);
  }, [expiresAt, now]);

  const valid = parsed !== null && parsed.expiresAt > now;
  return {
    ready: required !== null,
    locked: required === true && !valid,
    role: required && valid ? (parsed.grant === OWNER_GRANT ? 'owner' : 'guest') : null,
    expiresAt: valid ? parsed.expiresAt : null,
  };
}
